/**
 * Tabela de rotas da aplicação.
 *
 * ## Por que cada tela aparece duas vezes
 *
 * `/termos.html` e `/privacidade.html` estão registradas no App Registration do
 * Entra e aparecem na tela de consentimento da Microsoft. `/entrar.html` é o
 * destino do redirecionamento de erro em `auth/routes.js`.
 *
 * Trocar essas URLs não é edição nossa: as duas primeiras exigem pedido formal
 * ao IAM (`DEP-27`). Então elas continuam valendo, e as versões sem `.html`
 * existem ao lado para o que for escrito daqui em diante.
 *
 * Quando o registro do Entra for refeito, apontando para os caminhos limpos, as
 * linhas `.html` podem sair — e não antes.
 */
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Catalog } from './pages/Catalog.jsx';
import { RequestList } from './pages/RequestList.jsx';
import { Sites } from './pages/Sites.jsx';
import { SiteDetail } from './pages/SiteDetail.jsx';
import { Status } from './pages/Status.jsx';
import { Admin } from './pages/Admin.jsx';
import { SignIn } from './pages/SignIn.jsx';
import { Terms } from './pages/Terms.jsx';
import { Privacy } from './pages/Privacy.jsx';
import { NotFound } from './pages/NotFound.jsx';
import { TourProvider } from './tour/motor.jsx';

/**
 * `/paginas/<id>` → `/artefatos/<id>`.
 *
 * `<Navigate>` sozinho não serve: ele não sabe o id. Um componente de três
 * linhas lê o parâmetro e reconstrói o destino.
 */
function RedirecionaArtefato() {
  const { id } = useParams();
  return <Navigate to={`/artefatos/${id}`} replace />;
}

export function App() {
  return (
    <TourProvider>
      <Routes>
        {/* O portfólio é a raiz desde 26/08. O painel de status seguiu para
          /status, que já era endereço dele — por isso a troca não quebrou
          link nenhum. */}
        {/* Agentes e Sistemas sao secoes irmas, nao filtro de uma lista so:
          cada uma tem contagem, abas e busca proprias. A raiz e Agentes,
          que e onde a maioria das pessoas quer chegar. */}
        <Route path="/" element={<Catalog kind="AGENT" />} />
        <Route path="/agentes" element={<Catalog kind="AGENT" />} />
        <Route path="/sistemas" element={<Catalog kind="SYSTEM" />} />
        <Route path="/solicitacoes" element={<RequestList />} />

        {/* Artefatos: as páginas HTML que os usuários publicam.
          ⚠ O espaço de nomes do conteúdo publicado é `pagina-<uuid>`, servido
          pelo Express FORA do React, e nenhuma rota daqui pode começar com
          `pagina-` — há teste que reprova. `artefatos` não chega perto. */}
        <Route path="/artefatos" element={<Sites />} />
        <Route path="/artefatos/:id" element={<SiteDetail />} />

        {/* A seção se chamava "Páginas" até 19/10. Os endereços antigos
          continuam valendo porque já circularam em link e em favorito —
          custa duas linhas e evita um 404 para quem guardou. */}
        <Route path="/paginas" element={<Navigate to="/artefatos" replace />} />
        <Route path="/paginas/:id" element={<RedirecionaArtefato />} />

        <Route path="/status" element={<Status />} />

        {/* A rota existe para todo mundo; quem recusa e o servidor, com 403.
          Ver o comentario em `api.js`. */}
        <Route path="/admin" element={<Admin />} />

        <Route path="/entrar" element={<SignIn />} />
        <Route path="/entrar.html" element={<SignIn />} />

        <Route path="/termos" element={<Terms />} />
        <Route path="/termos.html" element={<Terms />} />

        <Route path="/privacidade" element={<Privacy />} />
        <Route path="/privacidade.html" element={<Privacy />} />

        {/* Endereço antigo do painel, de quando ele morava em index.html. */}
        <Route path="/index.html" element={<Navigate to="/" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </TourProvider>
  );
}
